import { useCallback, useEffect, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { Hotkey } from 'config/enums';
import { getDialPadVariantFromLocalStorage } from 'features/dial-pad/helpers';
import {
  clearAndCloseDialPad,
  clearDialedNumber,
  isDialPadOpenedSelector,
  selectDialedNumberOnNumberPad,
  setDialPadVariant,
} from 'features/dial-pad/store';
import {
  clearDialpadShareTextThunk,
  selectShareTextDialpadText,
} from 'features/share-text/store';

export const useDialPadOperations = () => {
  const dispatch = useAppDispatch();
  const dialedNumber = useAppSelector(selectDialedNumberOnNumberPad);
  const shareTextDialpadText = useAppSelector(selectShareTextDialpadText);
  const isDialPadOpened = useAppSelector(isDialPadOpenedSelector);

  const dialedNumberRef = useRef('');
  const shareTextDialpadTextRef = useRef('');
  const wasDialPadOpenedRef = useRef(isDialPadOpened);

  // Initialize dial pad variant from localStorage
  useEffect(() => {
    const dialPadVariantFromLocalStorage = getDialPadVariantFromLocalStorage();
    if (dialPadVariantFromLocalStorage) {
      dispatch(setDialPadVariant(dialPadVariantFromLocalStorage));
    }
  }, [dispatch]);

  // Keep ref synced with dialed number
  useEffect(() => {
    dialedNumberRef.current = dialedNumber;
  }, [dialedNumber]);

  useEffect(() => {
    shareTextDialpadTextRef.current = shareTextDialpadText;
  }, [shareTextDialpadText]);

  const clearDialedNumbers = useCallback(() => {
    if (dialedNumberRef.current || shareTextDialpadTextRef.current) {
      if (shareTextDialpadTextRef.current) {
        dispatch(clearDialpadShareTextThunk());
      }
      dispatch(clearDialedNumber());
    }
  }, [dispatch]);

  const clearAllAndCloseDialPad = useCallback(() => {
    if (dialedNumberRef.current || shareTextDialpadTextRef.current) {
      if (shareTextDialpadTextRef.current) {
        dispatch(clearDialpadShareTextThunk());
      }
      dispatch(clearDialedNumber());
    }
    dispatch(clearAndCloseDialPad());
  }, [dispatch]);

  // Auto-clear and close when dial pad closes with content
  useEffect(() => {
    const wasDialPadOpened = wasDialPadOpenedRef.current;
    wasDialPadOpenedRef.current = isDialPadOpened;

    if (!wasDialPadOpened || isDialPadOpened) {
      return;
    }

    if (
      dialedNumberRef.current?.length > 0 ||
      shareTextDialpadTextRef.current?.length > 0
    ) {
      clearAllAndCloseDialPad();
    }
  }, [clearAllAndCloseDialPad, isDialPadOpened]);

  // Delete key clears dial pad input and shared text (same as the X button)
  useHotkeys(
    Hotkey.ClearDialPad,
    (event) => {
      event.preventDefault();
      clearDialedNumbers();
    },
    {
      enabled: isDialPadOpened,
      enableOnFormTags: ['input', 'select', 'textarea'],
    }
  );

  return {
    dialedNumber,
    clearDialedNumbers,
    clearAllAndCloseDialPad,
  };
};
